import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';

const API = 'https://api.glowytics.com';

export default function SiteMetaCrawlerLab() {
  const [url, setUrl] = useState('');
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const [mode, setMode] = useState('auto');
  const [manualUrls, setManualUrls] = useState('');

  useEffect(() => {
    if (loading) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  const handleCrawl = async () => {
    if (mode === 'auto') {
      const trimmed = (url || '').trim();
      if (!trimmed) { toast.error('Lütfen bir site URL\'i girin'); return; }
      setLoading(true);
      setData(null);
      try {
        const res = await fetch(`${API}/api/labs/tools/crawl-site-meta`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed, limit: Math.min(200, Math.max(1, limit || 50)) }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || json?.details || `Request failed (${res.status})`);
        setData(json);
        toast.success(`${json.total || 0} sayfa tarandı`);
      } catch (e) {
        toast.error(e.message || 'Tarama başarısız');
        setData({ error: e.message });
      } finally { setLoading(false); }
    } else {
      const urls = manualUrls.split('\n').map(u => u.trim()).filter(u => u && u.startsWith('http'));
      if (urls.length === 0) { toast.error('En az bir geçerli URL girin (http/https ile başlamalı)'); return; }
      setLoading(true);
      setData(null);
      try {
        const res = await fetch(`${API}/api/labs/tools/crawl-site-meta`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls, limit: Math.min(200, urls.length) }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || json?.details || `Request failed (${res.status})`);
        setData(json);
        toast.success(`${json.total || 0} sayfa tarandı`);
      } catch (e) {
        toast.error(e.message || 'Tarama başarısız');
        setData({ error: e.message });
      } finally { setLoading(false); }
    }
  };

  const handleExportCSV = () => {
    if (!data?.results?.length) return;
    const header = 'URL,Title,Description,H1';
    const esc = (s) => '"' + (s || '').replace(/"/g, '""') + '"';
    const rows = data.results.map(r => [esc(r.url), esc(r.title), esc(r.description), esc(r.h1)].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `site-meta-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Labs · Site Title & Description Tarayıcı</h1>
        <p className="text-sm text-gray-500 mt-1">
          Web sitesinden sayfa title ve meta description değerlerini çeker.
        </p>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-4">
        <div className="flex gap-2 mb-2">
          <button
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${mode === 'auto' ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-gray-100 text-gray-600 border'}`}
            onClick={() => setMode('auto')}
          >
            Otomatik Tarama
          </button>
          <button
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${mode === 'manual' ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-gray-100 text-gray-600 border'}`}
            onClick={() => setMode('manual')}
          >
            URL Listesi Yapıştır
          </button>
        </div>

        {mode === 'auto' ? (
          <>
            <p className="text-xs text-gray-500">Sitemap.xml'den URL'leri otomatik bulur. Cloudflare korumalı sitelerde çalışmayabilir — bu durumda "URL Listesi Yapıştır" modunu kullanın.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Site URL</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Maks. sayfa</label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  min={1}
                  max={200}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500">Her satıra bir URL yazın. Cloudflare korumalı sitelerin sayfalarını tarayıcınızdan kopyalayıp buraya yapıştırabilirsiniz.</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">URL Listesi (her satıra bir URL)</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono min-h-[120px]"
                value={manualUrls}
                onChange={(e) => setManualUrls(e.target.value)}
                placeholder={"https://www.yenilio.com/\nhttps://www.yenilio.com/hakkimizda\nhttps://www.yenilio.com/urunler"}
              />
              <div className="text-xs text-gray-400 mt-1">
                {manualUrls.split('\n').filter(u => u.trim() && u.trim().startsWith('http')).length} geçerli URL
              </div>
            </div>
          </>
        )}

        <div className="flex gap-3">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            onClick={handleCrawl}
            disabled={loading}
          >
            {loading ? `Taranıyor… (${elapsed}s)` : 'Tara'}
          </button>
          <button
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            onClick={() => { setData(null); setUrl(''); setManualUrls(''); }}
            disabled={loading}
          >
            Temizle
          </button>
        </div>
      </div>

      {data?.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {data.error}
        </div>
      )}

      {data?.cloudflareBlocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          <strong>Cloudflare koruması algılandı.</strong> Bu site bot erişimini engelliyor.
          "URL Listesi Yapıştır" moduna geçip sayfaları elle ekleyerek tarayabilirsiniz.
        </div>
      )}

      {data?.results && data.results.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Sonuçlar ({data.total} sayfa) {data.baseUrl ? `— ${data.baseUrl}` : ''}
            </h2>
            <div className="flex items-center gap-2">
              {data.usedBrowser && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Playwright ile tarandı</span>
              )}
              <button onClick={handleExportCSV} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full hover:bg-green-200">
                CSV İndir
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">#</th>
                  <th className="text-left px-4 py-2 font-medium">URL</th>
                  <th className="text-left px-4 py-2 font-medium">Title</th>
                  <th className="text-left px-4 py-2 font-medium">Description</th>
                  <th className="text-left px-4 py-2 font-medium">H1</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r, i) => (
                  <tr key={i} className={`border-t hover:bg-gray-50 ${r.error ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-4 py-2">
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline truncate max-w-[200px] block" title={r.url}>
                        {r.url}
                      </a>
                    </td>
                    <td className="px-4 py-2 max-w-[220px]">
                      <span className={r.title ? '' : 'text-gray-400'} title={r.title}>
                        {(r.title || '(boş)').substring(0, 80)}{(r.title || '').length > 80 ? '…' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2 max-w-[280px]">
                      <span className={r.description ? '' : 'text-gray-400'} title={r.description}>
                        {(r.description || '(boş)').substring(0, 100)}{(r.description || '').length > 100 ? '…' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2 max-w-[200px]">
                      <span className={r.h1 ? '' : 'text-gray-400'} title={r.h1}>
                        {(r.h1 || '(boş)').substring(0, 60)}{(r.h1 || '').length > 60 ? '…' : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data?.results && data.results.length === 0 && !data?.error && !data?.cloudflareBlocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          Hiç sayfa bulunamadı. Sitemap veya ana sayfa linkleri kontrol edin.
        </div>
      )}
    </div>
  );
}
